import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, User, LogIn, LogOut, Globe, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import {
  changeGoogleTranslateLanguage,
  getCurrentGoogleTranslateLanguage,
  preloadGoogleTranslate,
} from '../utils/googleTranslate';

const languages = [
  { code: 'EN', name: 'English', gtCode: 'en' },
  { code: 'SV', name: 'Svenska', gtCode: 'sv' },
  { code: 'ES', name: 'Español', gtCode: 'es' },
  { code: 'DE', name: 'Deutsch', gtCode: 'de' },
];

const getInitialLanguage = () => {
  const currentLanguage = getCurrentGoogleTranslateLanguage();
  return languages.find((lang) => lang.gtCode === currentLanguage)?.code || 'EN';
};

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [language, setLanguage] = useState(getInitialLanguage);
  
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoggedIn, hasSubscription, isAdmin, logoutUser } = useAuth();

  const handleLogout = () => {
    logoutUser();
    navigate('/login/');
  };

  const changeLanguage = (gtCode: string) => {
    void changeGoogleTranslateLanguage(gtCode);
  };

  const handleLangChange = (lang: (typeof languages)[0]) => {
    setLanguage(lang.code);
    changeLanguage(lang.gtCode);
    setLangOpen(false);
  };

  const toggleLangOpen = () => {
    const nextOpen = !langOpen;
    setLangOpen(nextOpen);
    if (nextOpen) {
      void preloadGoogleTranslate();
    }
  };
  const navLinks = isAdmin ? [
    { name: 'Profile', path: '/account/' },
    { name: 'Live Classes', path: '/live-classes/' },
  ] : [
    { name: 'Home', path: '/' },
    { name: 'Library', path: '/library/' },
    { name: 'Pricing', path: '/pricing/' },
    { name: 'Unsubscribe', path: '/unsubscribe/' },
  ].filter((link) => !(isLoggedIn && hasSubscription && (link.path === '/pricing/' || link.path === '/pricing')));

  return (
    <header className="w-full relative z-50 bg-zinc-950/80 backdrop-blur-md border-b border-zinc-800/50 py-4 transition-all duration-300">
      <div className="max-w-7xl mx-auto px-6 xl:px-12">
        <div className="relative flex justify-between items-center">
          
          {/* LOGO */}
          <Link to="/" className="relative z-10 flex shrink-0 items-center gap-2 group" aria-label="Learn AI Hub home">
            <div className="absolute -inset-2 bg-gradient-to-r from-amber-500/20 to-amber-500/20 rounded-full blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <img
              src="/logo-learn-ai-hub.png"
              alt="Learn AI Hub"
              width="140"
              height="40"
              className="relative z-10 h-10 w-auto"
            />
          </Link>

          {/* DESKTOP NAV (Hidden on 1024px and below, shown on xl screens) */}
          <div className="absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 xl:flex">
            <nav className="flex w-fit items-center gap-8 rounded-full border border-zinc-800/50 bg-zinc-900/50 px-8 py-2.5 shadow-inner">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.path || (link.path !== '/' && location.pathname === link.path.replace(/\/+$/, ''));
                return (
                  <Link 
                    key={link.name}
                    to={link.path} 
                    className={`relative text-xs font-bold uppercase tracking-widest transition-colors duration-300 ${
                      isActive ? 'text-amber-500' : 'text-zinc-400 hover:text-white'
                    } group`}
                  >
                    {link.name}
                    <span className={`absolute -bottom-2 left-1/2 -translate-x-1/2 h-[2px] rounded-full bg-amber-500 transition-all duration-300 ${
                      isActive ? 'w-full opacity-100' : 'w-0 opacity-0 group-hover:w-full group-hover:opacity-100'
                    }`}></span>
                  </Link>
                );
              })}
            </nav>
          </div>

          {/* CTA & ACCOUNT (Hidden on 1024px and below, shown on xl screens) */}
          <div className="relative z-10 hidden shrink-0 xl:flex items-center gap-[0.5rem]">
            
            {/* LANGUAGE SELECTOR (Desktop) */}
            <div className="relative mr-4 xl:mr-7 2xl:mr-5">
              <button
                type="button"
                onClick={toggleLangOpen}
                className="flex min-h-11 items-center gap-1.5 text-zinc-300 hover:text-amber-500 transition-colors font-bold text-xs uppercase tracking-widest px-3"
                aria-expanded={langOpen}
                aria-label="Change language"
              >
                <Globe className="w-4 h-4" />
                {language}
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${langOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Language Dropdown Menu */}
              {langOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setLangOpen(false)}
                  />
                  <div className="absolute right-0 top-full mt-2 w-36 rounded-xl border border-zinc-800 bg-zinc-950/95 py-2 shadow-2xl backdrop-blur-xl z-50">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => handleLangChange(lang)}
                        type="button"
                        className={`w-full px-4 py-2 text-left text-xs font-bold uppercase tracking-wider transition-colors hover:bg-zinc-800/60 ${
                          language === lang.code ? 'text-amber-400' : 'text-zinc-300 hover:text-white'
                        }`}
                      >
                        {lang.name}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {isLoggedIn ? (
              <>
                <Link
                  to="/account/"
                  className={`group inline-flex items-center transition-colors ${
                    location.pathname === '/account' || location.pathname === '/account/' ? 'text-amber-400' : 'text-zinc-300 hover:text-[#f59e0b]'
                  }`}
                  aria-label="My Account"
                  title="My Account"
                >
                  <span className="w-9 h-9 rounded-full flex items-center justify-center border bg-[#F59E0B] border-[#F59E0B] text-zinc-950">
                    <User className="w-4 h-4" />
                  </span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="whitespace-nowrap group relative flex items-center gap-2 bg-[#f59e0b] text-zinc-950 px-6 py-2.5 rounded-full font-black text-xs uppercase tracking-widest transition-all hover:-translate-y-0.5"
                >
                  <LogOut className="w-5 h-5 text-black" />
                  Log out
                </button>
              </>
            ) : (
              <Link to="/login/" className="whitespace-nowrap group relative flex items-center gap-2 bg-[#f59e0b] text-zinc-950 px-6 py-2.5 rounded-full font-black text-xs uppercase tracking-widest transition-all hover:-translate-y-0.5">
                <LogIn className="w-5 h-5 text-black" />
                Log in
              </Link>
            )}
          </div>

          {/* MOBILE/TABLET TOGGLE (Shown on screens smaller than xl - 1280px) */}
          <button 
            type="button"
            className="xl:hidden relative min-h-11 min-w-11 p-2 text-zinc-300 hover:text-[#f59e0b] transition-colors focus:outline-none"
            onClick={() => setIsOpen(!isOpen)}
            aria-expanded={isOpen}
            aria-controls="mobile-navigation"
            aria-label={isOpen ? 'Close navigation menu' : 'Open navigation menu'}
            title={isOpen ? 'Close navigation menu' : 'Open navigation menu'}
          >
            <div className="absolute inset-0 bg-[#f59e0b]/10 rounded-lg scale-0 hover:scale-100 transition-transform duration-300"></div>
            <span className="sr-only">{isOpen ? 'Close navigation menu' : 'Open navigation menu'}</span>
            {isOpen ? <X aria-hidden="true" className="w-7 h-7 relative z-10" /> : <Menu aria-hidden="true" className="w-7 h-7 relative z-10" />}
          </button>
        </div>

        {/* MOBILE/TABLET MENU */}
        <div 
          id="mobile-navigation"
          className={`xl:hidden absolute top-full left-0 w-full bg-zinc-950/95 backdrop-blur-xl border-b border-zinc-800/50 transition-all duration-300 ease-in-out origin-top overflow-hidden ${
            isOpen ? 'max-h-[600px] opacity-100 py-6 shadow-2xl' : 'max-h-0 opacity-0 py-0'
          }`}
        >
          <nav className="flex flex-col gap-2 px-6">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.path || (link.path !== '/' && location.pathname === link.path.replace(/\/+$/, ''));
              return (
                <Link 
                  key={link.name}
                  to={link.path} 
                  className={`text-xl font-black uppercase tracking-tighter transition-colors py-3 border-b border-zinc-800/50 ${
                    isActive ? 'text-amber-500' : 'text-zinc-300 hover:text-white'
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  {link.name}
                </Link>
              );
            })}
              
              <div className="h-px bg-zinc-800 my-2"></div>

              {/* LANGUAGE SELECTOR (Mobile) */}
              <div className="flex items-center gap-4 py-3">
                <Globe className="w-5 h-5 text-zinc-500" />
                <div className="flex gap-4">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLangChange(lang)}
                      type="button"
                      aria-label={`Switch language to ${lang.name}`}
                      className={`min-h-11 min-w-11 text-sm font-black uppercase tracking-widest transition-colors ${
                        language === lang.code ? 'text-amber-500' : 'text-zinc-300 hover:text-white'
                      }`}
                    >
                      {lang.code}
                    </button>
                  ))}
                </div>
              </div>

              <div className="h-px bg-zinc-800 mb-4 mt-2"></div>
              
              {isLoggedIn ? (
                <div className="flex flex-wrap items-center gap-4">
                  <Link
                    to="/account/"
                    className="inline-flex items-center gap-3 text-lg font-black text-[#f59e0b] uppercase tracking-tighter"
                    onClick={() => setIsOpen(false)}
                  >
                    <span className="w-10 h-10 rounded-xl bg-[#f59e0b] text-zinc-950 flex items-center justify-center">
                      <User className="w-5 h-5" />
                    </span>
                    My Account
                  </Link>
                  <button
                    type="button"
                    onClick={() => { setIsOpen(false); handleLogout(); }}
                    className="whitespace-nowrap group relative flex items-center gap-2 bg-[#f59e0b] text-zinc-950 px-6 py-2.5 rounded-full font-black text-xs uppercase tracking-widest transition-all hover:shadow-[0_0_20px_rgba(163,230,53,0.4)] hover:-translate-y-0.5"
                  >
                     <LogOut className="w-5 h-5 text-black" />
                    Log out
                  </button>
                </div>
              ) : (
                <Link 
                  to="/login/" 
                  onClick={() => setIsOpen(false)}
                  className="whitespace-nowrap group relative inline-flex items-center gap-2 bg-[#f59e0b] text-zinc-950 px-6 py-2.5 rounded-full font-black text-xs uppercase tracking-widest transition-all w-max hover:shadow-[0_0_20px_rgba(163,230,53,0.4)] hover:-translate-y-0.5"
                >
                  <LogIn className="w-5 h-5 text-black" />
                  Log in
                </Link>
              )}
            </nav>
          </div>
      </div>
    </header>
  );
};

export default Header;
